import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { FaSync, FaChevronDown } from 'react-icons/fa';
import Layout from '../components/Layout';
import Filter from '../components/Filter';
import ProductCard from '../components/ProductCard';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import '../css/ProductPage.css';
import { useCart } from '../context/CartContext';
import { BACKEND_URL } from '../config';
import { getCanonicalUrl } from '../utils/canonicalUrl';
import { CATEGORY_SEO } from '../config/categoryFilters';

/**
 * Константи для конфігурації
 */
const INITIAL_PAGE = 0;
const LOADING_TEXT = 'Завантаження...';
const LOAD_MORE_TEXT = 'Завантажити більше';
const EMPTY_STATE_MESSAGE = 'Товари не знайдено';

// ============================================================================
// SLUG HELPERS
// ============================================================================

/**
 * Slug бренду: поле slug або name.toLowerCase().replace(/ /g, '-')
 */
const getBrandSlug = (brand) =>
  brand?.slug || brand?.name?.toLowerCase().replace(/\s+/g, '-') || null;

/**
 * ProductPage - Сторінка з каталогом продуктів та фільтрацією
 *
 * URL параметри (slug-based):
 * - ?brand=bmw&model=e39&category=akpp&minPrice=X&maxPrice=Y&inStock=true
 *
 * Логіка:
 * 1. Зчитуємо slug-параметри з URL.
 * 2. Завантажуємо brands + categories один раз при монтуванні.
 * 3. Резолвимо slug → numeric ID (resolvedIds) асинхронно.
 * 4. Тільки коли resolvedIds готові — викликаємо API /products/by-filters з ID.
 * 5. Filter отримує resolvedIds як initialFilters і відображає правильний стан.
 * 6. При зміні фільтра Filter відправляє slug → ProductPage оновлює URL.
 */
const ProductPage = () => {
  // ============================================================================
  // HOOKS AND ROUTING
  // ============================================================================

  const {
    category: categoryPathParam,
    brand:    brandPathParam,
    model:    modelPathParam,
    category_id,
    model_id,
  } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { addToCart, setIsCartOpen } = useCart();

  // ============================================================================
  // EXTRACT URL SLUG PARAMETERS
  // ============================================================================

  /**
   * Slug-параметри фільтрів з URL.
   * category: path param (/zapchastyny/:category) має пріоритет над query param.
   */
  const urlFilters = useMemo(() => ({
    brand:        brandPathParam    || searchParams.get('brand'),
    model:        modelPathParam    || searchParams.get('model'),
    category:     categoryPathParam || searchParams.get('category'),
    minPrice:     searchParams.get('minPrice'),
    maxPrice:     searchParams.get('maxPrice'),
    inStock:      searchParams.get('inStock'),
    year:         searchParams.get('year'),
    engine:       searchParams.get('engine'),
    manufacturer: searchParams.get('manufacturer'),
    q:            searchParams.get('q'),
  }), [searchParams, categoryPathParam, brandPathParam, modelPathParam]);

  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================

  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(INITIAL_PAGE);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);

  /** Завантажені списки брендів і категорій (для резолва slug → ID) */
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);

  /**
   * resolvedIds: числові ID + назви, отримані з slug URL.
   * null = ще не резолвилось (продукти не завантажуємо).
   * { brandId, modelId, categoryId, resolvedBrandName, resolvedModelName } = готово.
   */
  const [resolvedIds, setResolvedIds] = useState(null);

  /** SEO metadata */
  const [pageMetadata, setPageMetadata] = useState({
    categoryName: '',
    modelName: '',
    brandName: '',
  });

  // ============================================================================
  // LOAD BRANDS AND CATEGORIES (one-time, for slug resolution)
  // ============================================================================

  useEffect(() => {
    fetch(`${BACKEND_URL}/categories/brands`)
      .then(r => r.ok ? r.json() : [])
      .then(setBrands)
      .catch(() => setBrands([]));

    fetch(`${BACKEND_URL}/categories`)
      .then(r => r.ok ? r.json() : [])
      .then(setCategories)
      .catch(() => setCategories([]));
  }, []);

  // ============================================================================
  // SLUG → ID RESOLUTION
  // ============================================================================

  useEffect(() => {
    // Скидаємо до null — продукти не завантажуватимуться поки не резолвимось
    setResolvedIds(null);

    const resolve = async () => {
      const { brand: brandSlug, model: modelSlug, category: catSlug } = urlFilters;

      // Якщо взагалі немає slug-фільтрів — одразу резолвимо з null ID
      if (!brandSlug && !catSlug && !modelSlug && !category_id && !model_id) {
        setResolvedIds({ brandId: null, modelId: null, categoryId: null, resolvedBrandName: '', resolvedModelName: '' });
        return;
      }

      // Для legacy URL params (path params)
      if (!brandSlug && !catSlug && !modelSlug) {
        setResolvedIds({
          brandId:    null,
          modelId:    model_id    ? Number(model_id)    : null,
          categoryId: category_id ? Number(category_id) : null,
          resolvedBrandName: '',
          resolvedModelName: '',
        });
        return;
      }

      // Очікуємо завантаження брендів, якщо потрібні
      if (brandSlug && brands.length === 0) return;
      // Очікуємо завантаження категорій, якщо потрібні
      if (catSlug && categories.length === 0) return;

      let brandId            = null;
      let modelId            = null;
      let categoryId         = null;
      let resolvedBrandName  = '';
      let resolvedModelName  = '';

      if (brandSlug) {
        const found = brands.find(b => getBrandSlug(b) === brandSlug);
        brandId           = found?.id   || null;
        resolvedBrandName = found?.name || '';
      }

      if (catSlug) {
        const found = categories.find(c => c.slug === catSlug);
        categoryId = found?.id || null;
      }

      if (modelSlug && brandId) {
        try {
          const res = await fetch(`${BACKEND_URL}/categories/brands/${brandId}/models`);
          if (res.ok) {
            const modelsData = await res.json();
            const found = modelsData.find(m =>
              (m.slug?.toLowerCase() === modelSlug) ||
              (m.name?.toLowerCase().replace(/\s+/g, '-') === modelSlug)
            );
            modelId           = found?.id   || null;
            resolvedModelName = found?.name || '';
          }
        } catch {
          // Нічого — modelId залишається null
        }
      }

      setResolvedIds({ brandId, modelId, categoryId, resolvedBrandName, resolvedModelName });
    };

    resolve();
  }, [
    urlFilters.brand,
    urlFilters.model,
    urlFilters.category,
    brands,
    categories,
    category_id,
    model_id,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]);

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================

  /**
   * Побудова URLSearchParams для API запиту (використовує numeric resolvedIds)
   */
  const buildApiParams = useCallback((pageNum) => {
    const params = new URLSearchParams({ page: pageNum, pageSize: 40 });

    if (resolvedIds?.brandId)    params.append('brandId',    resolvedIds.brandId);
    if (resolvedIds?.modelId)    params.append('modelId',    resolvedIds.modelId);
    if (resolvedIds?.categoryId) params.append('categoryId', resolvedIds.categoryId);

    if (urlFilters.q)            params.append('q',            urlFilters.q);
    if (urlFilters.year)         params.append('year',         urlFilters.year);
    if (urlFilters.engine)       params.append('engine',       urlFilters.engine);
    if (urlFilters.manufacturer) params.append('manufacturer', urlFilters.manufacturer);

    if (urlFilters.minPrice) {
      const v = parseFloat(urlFilters.minPrice);
      if (!isNaN(v)) params.append('minPrice', v.toString());
    }
    if (urlFilters.maxPrice) {
      const v = parseFloat(urlFilters.maxPrice);
      if (!isNaN(v)) params.append('maxPrice', v.toString());
    }
    if (urlFilters.inStock === 'true') {
      params.append('inStock', 'true');
    }

    return params;
  }, [resolvedIds, urlFilters]);

  const isLastPage = useCallback((data) => {
    if (data.currentPage !== undefined && data.totalPages !== undefined) {
      return data.currentPage >= (data.totalPages - 1);
    }
    if (data.last !== undefined) return data.last;
    return true;
  }, []);

  const extractProducts = useCallback((data) => {
    return data.products || data.content || [];
  }, []);

  // ============================================================================
  // API FUNCTIONS
  // ============================================================================

  /**
   * Завантаження метаданих для SEO з уже завантажених масивів brands/categories
   */
  const fetchMetadata = useCallback(() => {
    if (!resolvedIds) return;

    const { categoryId, resolvedBrandName, resolvedModelName } = resolvedIds;
    const metadata = { categoryName: '', modelName: '', brandName: '' };

    if (categoryId) {
      metadata.categoryName = categories.find(c => c.id === categoryId)?.name || '';
    }

    // Назви бренду і моделі отримані під час slug-resolution — без окремих запитів
    metadata.brandName = resolvedBrandName || '';
    metadata.modelName = resolvedModelName || '';

    setPageMetadata(metadata);
  }, [resolvedIds, categories]);

  /**
   * Завантаження продуктів
   */
  const fetchProducts = useCallback(async (pageNum, isFirstLoad = false) => {
    setLoading(true);

    try {
      const params  = buildApiParams(pageNum);
      const apiUrl  = `${BACKEND_URL}/products/by-filters?${params.toString()}`;
      const response = await fetch(apiUrl);

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data            = await response.json();
      const fetchedProducts = extractProducts(data);

      if (isFirstLoad) {
        setProducts(fetchedProducts);
      } else {
        setProducts(prev => [...prev, ...fetchedProducts]);
      }

      setHasMore(!isLastPage(data));
    } catch (error) {
      console.error('Error fetching products:', error);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [buildApiParams, extractProducts, isLastPage]);

  // ============================================================================
  // EFFECTS
  // ============================================================================

  /**
   * Перезавантаження продуктів коли resolvedIds або цінові фільтри змінились.
   * resolvedIds === null означає "ще резолвимось" → не завантажуємо.
   */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchProductsRef = useRef(fetchProducts);
  useEffect(() => { fetchProductsRef.current = fetchProducts; }, [fetchProducts]);

  useEffect(() => {
    if (resolvedIds === null) return;
    setPage(INITIAL_PAGE);
    setProducts([]);
    setHasMore(true);
    fetchProductsRef.current(INITIAL_PAGE, true);
  }, [
    resolvedIds,
    urlFilters.q,
    urlFilters.minPrice,
    urlFilters.maxPrice,
    urlFilters.inStock,
    urlFilters.year,
    urlFilters.engine,
    urlFilters.manufacturer,
  ]);

  /** Метадані для SEO — оновлюються разом із resolvedIds */
  useEffect(() => {
    fetchMetadata();
  }, [fetchMetadata]);

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  /**
   * Отримує slug-фільтри від Filter і оновлює URL.
   * Категорія → path param, решта → query params.
   */
  const handleApplyFilter = useCallback((filters) => {
    // Продуктові та цінові фільтри завжди як query params
    const params = new URLSearchParams();
    if (filters.minPrice) params.set('minPrice', filters.minPrice.toString());
    if (filters.maxPrice) params.set('maxPrice', filters.maxPrice.toString());
    if (filters.inStock === true) params.set('inStock', 'true');
    if (filters.year)         params.set('year',         filters.year);
    if (filters.engine)       params.set('engine',       filters.engine);
    if (filters.manufacturer) params.set('manufacturer', filters.manufacturer);

    let path;
    if (filters.category) {
      // Якщо є категорія — brand і model йдуть у path
      path = `/zapchastyny/${filters.category}`;
      if (filters.brand) {
        path += `/${filters.brand}`;
        if (filters.model) path += `/${filters.model}`;
      }
    } else {
      // Без категорії — brand і model як query params
      path = '/zapchastyny';
      if (filters.brand) params.set('brand', filters.brand);
      if (filters.model) params.set('model', filters.model);
    }

    const qs = params.toString();
    navigate(qs ? `${path}?${qs}` : path, { replace: true });
  }, [navigate]);

  const handleProductClick = useCallback((slugOrId, event) => {
    event.stopPropagation();
    navigate(`/products/${slugOrId}`);
  }, [navigate, categoryPathParam]);

  const handleBuyClick = useCallback((product, event) => {
    event.stopPropagation();
    addToCart({
      id:      product.id,
      slug:    product.slug,
      name_ua: product.name_ua,
      price:   product.price,
      photo:   product.photo,
    });
    setIsCartOpen(true);
    if (window.gtag) {
        window.gtag('event', 'add_to_cart', {
            currency: 'UAH',
            value: parseFloat((product.price || 0).toFixed(2)),
            items: [{
                item_id: String(product.id),
                item_name: product.name_ua,
                price: parseFloat((product.price || 0).toFixed(2)),
                quantity: 1,
                google_business_vertical: 'retail',
            }],
        });
    }
  }, [addToCart, setIsCartOpen]);

  const handleLoadMore = useCallback(() => {
    if (loading || !hasMore) return;
    const nextPage = page + 1;
    setPage(nextPage);
    fetchProducts(nextPage, false);
  }, [loading, hasMore, page, fetchProducts]);

  // ============================================================================
  // MEMOIZED VALUES
  // ============================================================================

  /**
   * Початкові значення для Filter — slug-параметри з URL.
   * Передаються напряму, не залежать від resolvedIds.
   */
  const initialFilters = useMemo(() => ({
    brand:        urlFilters.brand        || null,
    model:        urlFilters.model        || null,
    category:     urlFilters.category     || null,
    minPrice:     urlFilters.minPrice     || null,
    maxPrice:     urlFilters.maxPrice     || null,
    inStock:      urlFilters.inStock === 'true' ? true : null,
    year:         urlFilters.year         || null,
    engine:       urlFilters.engine       || null,
    manufacturer: urlFilters.manufacturer || null,
  }), [urlFilters]);

  const showEmptyState      = !loading && products.length === 0 && resolvedIds !== null;
  const showInitialLoading  = (loading && products.length === 0) || resolvedIds === null;

  const seoData = useMemo(() => {
    const { categoryName, modelName, brandName } = pageMetadata;
    const catSeo    = CATEGORY_SEO[urlFilters.category] || {};
    const synonyms  = catSeo.synonyms || '';

    // Пошуковий запит — окрема гілка
    if (urlFilters.q) {
      return {
        title:       `Результати пошуку «${urlFilters.q}»`,
        description: `Результати пошуку за запитом «${urlFilters.q}» в інтернет-магазині UnionAuto.`,
        h1:          `Результати пошуку «${urlFilters.q}»`,
        isSearch:    true,
      };
    }

    let title       = 'Каталог автозапчастин';
    let description = 'Широкий вибір якісних автозапчастин та аксесуарів в інтернет-магазині UnionAuto.';
    let h1          = '';

    if (modelName && brandName) {
      title       = `Запчастини ${brandName} ${modelName}`;
      description = `Купити оригінальні та якісні запчастини для ${brandName} ${modelName} в UnionAuto. Доступні ціни, швидка доставка по Україні.`;
      h1          = title;
    } else if (brandName) {
      title       = `Автозапчастини ${brandName}`;
      description = `Каталог оригінальних запчастин для автомобілів ${brandName}. Великий асортимент, гарантія якості, доставка по Україні.`;
      h1          = title;
    } else if (modelName) {
      title       = `Запчастини для ${modelName}`;
      description = `Оригінальні запчастини для ${modelName} за доступними цінами в UnionAuto. Швидка доставка по всій Україні.`;
      h1          = title;
    }

    if (categoryName) {
      const brandModel   = [brandName, modelName].filter(Boolean).join(' ');
      const manufacturer = urlFilters.manufacturer || '';
      // Специфікатор: або "для Honda Civic IX", або "Denso" (для категорій без авто)
      const specPart     = brandModel
        ? ` для ${brandModel}`
        : manufacturer ? ` ${manufacturer}` : '';

      const synPart = synonyms ? ` (${synonyms})` : '';
      title       = `${categoryName}${synPart}${specPart}`;
      h1          = `Купити ${categoryName.charAt(0).toLowerCase() + categoryName.slice(1)}${specPart}`;
      description = `${categoryName}${synonyms ? `, ${synonyms}` : ''}${specPart} - купити в UnionAuto за вигідною ціною. Оригінальні деталі, гарантія якості, швидка доставка по Україні.`;
    }

    return { title, description, h1, isSearch: false };
  }, [pageMetadata, urlFilters.category, urlFilters.manufacturer, urlFilters.q]);

  const canonicalUrl = useMemo(() => {
    return getCanonicalUrl(
      categoryPathParam || null,
      brandPathParam    || null,
      modelPathParam    || null,
      searchParams
    );
  }, [categoryPathParam, brandPathParam, modelPathParam, searchParams]);

  const breadcrumbItems = useMemo(() => {
    const { categoryName, brandName, modelName } = pageMetadata;
    const catSlug   = urlFilters.category;
    const brandSlug = urlFilters.brand;
    const modelSlug = urlFilters.model;
    const items = [{ name: 'Головна', path: '/' }];

    if (categoryName) {
      items.push({
        name: categoryName,
        path: catSlug ? `/zapchastyny/${catSlug}` : null,
      });
    }

    if (brandName) {
      let brandPath = null;
      if (brandSlug) {
        brandPath = catSlug
          ? `/zapchastyny/${catSlug}/${brandSlug}`
          : `/zapchastyny?brand=${brandSlug}`;
      }
      items.push({ name: brandName, path: brandPath });
    }

    if (modelName) {
      let modelPath = null;
      if (brandSlug && modelSlug) {
        modelPath = catSlug
          ? `/zapchastyny/${catSlug}/${brandSlug}/${modelSlug}`
          : `/zapchastyny?brand=${brandSlug}&model=${modelSlug}`;
      }
      items.push({ name: modelName, path: modelPath });
    }

    if (urlFilters.q) {
      items.push({ name: `Пошук: «${urlFilters.q}»` });
    } else if (!categoryName && !brandName && !modelName) {
      items.push({ name: 'Каталог' });
    }

    return items;
  }, [pageMetadata, urlFilters]);

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <Layout>
      <SEO
        title={seoData.title}
        description={seoData.description}
        canonical={canonicalUrl}
        noindex={seoData.isSearch}
        structuredData={!seoData.isSearch ? {
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": seoData.title,
          "description": seoData.description,
          "url": typeof window !== 'undefined' ? window.location.href : 'https://union-auto.com.ua/search',
        } : undefined}
      />
      <main className="product-page" role="main" aria-label="Каталог товарів">
        <Breadcrumbs items={breadcrumbItems} />
        {seoData.h1 && (
          <h1 className="catalog-page-h1">{seoData.h1}</h1>
        )}
        <div className="product-content-wrapper">
          <aside className="product-sidebar" aria-label="Панель фільтрів">
            <Filter
              onApplyFilter={handleApplyFilter}
              initialFilters={initialFilters}
            />
          </aside>

          <section className="product-content" aria-live="polite" aria-atomic="true">
            {showInitialLoading && (
              <div className="loading-state" role="status" aria-label="Завантаження товарів">
                <p>{LOADING_TEXT}</p>
                <div className="loading-progress" aria-hidden="true"></div>
              </div>
            )}

            {showEmptyState && (
              <div className="empty-state" role="status" aria-label="Немає товарів">
                <p>{EMPTY_STATE_MESSAGE}</p>
                <p className="empty-state-hint">
                  Спробуйте змінити фільтри або пошуковий запит
                </p>
                <button
                  className="reset-filters-button"
                  onClick={() => navigate('/zapchastyny', { replace: true })}
                  type="button"
                  aria-label="Скинути всі фільтри"
                >
                  <FaSync className="reset-icon" />
                  <span>Скинути фільтри</span>
                </button>
              </div>
            )}

            {products.length > 0 && (
              <>
                <div className="product-grid" role="grid" aria-label="Сітка товарів">
                  {products.map((product) => (
                    <div key={product.id} role="gridcell">
                      <ProductCard
                        product={product}
                        onProductClick={handleProductClick}
                        onBuyClick={handleBuyClick}
                      />
                    </div>
                  ))}
                </div>

                {hasMore && (
                  <div className="load-more-container">
                    <button
                      className="load-more-button"
                      onClick={handleLoadMore}
                      disabled={loading}
                      type="button"
                      aria-label={loading ? 'Завантаження додаткових товарів' : 'Завантажити більше товарів'}
                    >
                      {loading ? (
                        <>
                          <FaSync className="loading-spinner" aria-hidden="true" />
                          <span>{LOADING_TEXT}</span>
                        </>
                      ) : (
                        <>
                          <FaChevronDown className="load-more-icon" aria-hidden="true" />
                          <span>{LOAD_MORE_TEXT}</span>
                        </>
                      )}
                    </button>
                  </div>
                )}

                {!hasMore && products.length > 0 && (
                  <div className="end-of-results" role="status" aria-live="polite">
                    <p>Це всі доступні товари</p>
                  </div>
                )}

                {!hasMore && products.length > 0 && (
                  <div className="catalog-contact-banner">
                    <span className="catalog-contact-banner__text">
                      💬 Не знайшли потрібне або потрібно більше фото? Напишіть нам — допоможемо.
                    </span>
                    <a href="/contacts" className="catalog-contact-banner__btn">
                      Зв'язатись
                    </a>
                  </div>
                )}
              </>
            )}
          </section>
        </div>
      </main>
    </Layout>
  );
};

export default ProductPage;
